import cn from "classnames";
import { Container } from "components/layout/Container";
import { Heading, HeadingProps } from "components/layout/Heading";
import { AccordionItem } from "components/shared/AccordionItem";
import styles from "./SectionFaq.module.scss";
import { SimpleContentParser } from "components/shared/SimpleContentParser";
import { AccordionItemWithEditorContent } from "../AccordionItemWithEditorContent";
import { parseEditorContentToString, prepareJsonLdString } from "@/helpers/common";
import { FAQPageJsonLd } from "next-seo";

type RegularFAQItem = {
  heading: string;
  content: string;
};

type EditorContentFAQItem = {
  question: string;
  answer: string;
};

export type SectionFaqProps = {
  heading: string;
  subheading?: string;
  longSubheading?: boolean;
  className?: string;
  image?: HeadingProps["image"];
  itemHeadingSize?: "default" | "big";
  microData?: boolean;
  faqItems: RegularFAQItem[] | EditorContentFAQItem[];
  editorContent?: boolean;
};

export const SectionFaq = ({
  heading,
  subheading,
  longSubheading,
  faqItems,
  microData = true,
  className,
  itemHeadingSize = "big",
  image,
  editorContent = false,
}: SectionFaqProps) => {
  const regularItems = faqItems as RegularFAQItem[];
  const editorContentItems = faqItems as EditorContentFAQItem[];

  const microDataAttributes = editorContent
    ? {}
    : microData
    ? {
        itemScope: true,
        itemType: "https://schema.org/FAQPage",
      }
    : {};

  const listJsonLd = editorContent
    ? editorContentItems.map(({ question, answer }) => ({
        questionName: prepareJsonLdString(question),
        acceptedAnswerText: prepareJsonLdString(parseEditorContentToString(JSON.parse(answer))),
      }))
    : [];

  const halfItemLength = Math.ceil(faqItems.length / 2);

  const regularFAQGroups = [
    regularItems.slice(0, halfItemLength),
    regularItems.slice(halfItemLength, regularItems.length),
  ];

  const editorContentFAQGroups = [
    editorContentItems.slice(0, halfItemLength),
    editorContentItems.slice(halfItemLength, editorContentItems.length),
  ];

  return (
    <section
      className={cn(styles.section, {
        [styles.sectionLongSubheading]: longSubheading,
        className,
      })}
    >
      {editorContent && <FAQPageJsonLd useAppDir mainEntity={listJsonLd} />}
      <Container className={styles.wrapper}>
        <Heading image={image} title={heading} subheading={subheading} className={styles.heading} />
        <div className={styles.faqItems} {...microDataAttributes}>
          {editorContent
            ? editorContentFAQGroups.map((group, index) => (
                <div key={index} className={styles.itemWrapper}>
                  {group.map((faqItem, i) => (
                    <AccordionItemWithEditorContent
                      key={i}
                      className={styles.faqItem}
                      heading={faqItem?.question}
                      headingSize={itemHeadingSize}
                      content={faqItem?.answer}
                    />
                  ))}
                </div>
              ))
            : regularFAQGroups.map((group, index) => (
                <div key={index} className={styles.itemWrapper}>
                  {group.map((faqItem, i) => (
                    <AccordionItem
                      key={i}
                      headingSize={itemHeadingSize}
                      className={styles.faqItem}
                      heading={faqItem.heading}
                      microData={microData}
                      active={!index && !i}
                      background
                    >
                      <SimpleContentParser content={faqItem.content} />
                    </AccordionItem>
                  ))}
                </div>
              ))}
        </div>
      </Container>
    </section>
  );
};

export default SectionFaq;
